function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useContext, useEffect } from "react";
import { change, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { isFunction } from "lodash";
import { withRouter } from "react-router-dom";
import { branch, compose } from "recompose";
import pureNoFunc from "../../utils/pureNoFunc";
import TableFormTrackerContext from "../TableFormTrackerContext";
import convertSchema from "./convertSchema";
import { getRecordsFromReduxForm } from "./withSelectedEntities";
import { makeDataTableHandlers, getQueryParams, setCurrentParamsOnUrl, getMergedOpts, getCurrentParamsFromUrl } from "./queryParams";
import getTableConfigFromStorage from "./getTableConfigFromStorage";
/**
 *  Note all these options can be passed at Design Time or at Runtime (like reduxForm())
 *
 * @export
 *
 * @param {compOrOpts} compOrOpts
 * @typedef {object} compOrOpts
 * @property {*string} formName - required unique identifier for the table
 * @property {Object | Function} schema - The data table schema or a function returning it. The function wll be called with props as the argument.
 * @property {boolean} urlConnected - whether the table should connect to/update the URL
 * @property {boolean} withSelectedEntities - whether or not to pass the selected entities
 * @property {boolean} isCodeModel - whether the model is keyed by code instead of id in the db
 * @property {object} defaults - tableParam defaults such as pageSize, filter, etc
 * @property {boolean} noOrderError - won't console an error if an order is not found on schema
 */

export default function withTableParams(compOrOpts, pTopLevelOpts) {
  var _this = this;

  var topLevelOptions;
  var Component;

  if (!pTopLevelOpts) {
    topLevelOptions = compOrOpts;
  } else {
    topLevelOptions = pTopLevelOpts;
    Component = compOrOpts;
  }

  var _topLevelOptions = topLevelOptions,
      isLocalCall = _topLevelOptions.isLocalCall;

  var mapStateToProps = function mapStateToProps(state, ownProps) {
    var _ref;

    var mergedOpts = getMergedOpts(topLevelOptions, ownProps);
    var history = mergedOpts.history,
        urlConnected = mergedOpts.urlConnected,
        withSelectedEntities = mergedOpts.withSelectedEntities,
        formName = mergedOpts.formName,
        formNameFromWithTPCall = mergedOpts.formNameFromWithTPCall,
        syncDisplayOptionsToDb = mergedOpts.syncDisplayOptionsToDb,
        defaults = mergedOpts.defaults,
        isInfinite = mergedOpts.isInfinite,
        isSimple = mergedOpts.isSimple,
        withPaging = mergedOpts.withPaging,
        doNotCoercePageSize = mergedOpts.doNotCoercePageSize,
        initialValues = mergedOpts.initialValues,
        _mergedOpts$additiona = mergedOpts.additionalFilter,
        additionalFilter = _mergedOpts$additiona === void 0 ? {} : _mergedOpts$additiona,
        _mergedOpts$additiona2 = mergedOpts.additionalOrFilter,
        additionalOrFilter = _mergedOpts$additiona2 === void 0 ? {} : _mergedOpts$additiona2,
        noOrderError = mergedOpts.noOrderError,
        withDisplayOptions = mergedOpts.withDisplayOptions,
        cellRenderer = mergedOpts.cellRenderer,
        model = mergedOpts.model,
        isCodeModel = mergedOpts.isCodeModel,
        noForm = mergedOpts.noForm;
    var schema = getSchema(mergedOpts);

    if (ownProps.isTableParamsConnected) {
      if (formName && formNameFromWithTPCall && formName !== formNameFromWithTPCall) {
        console.error("You passed a formName prop, " + formName + " to a <DataTable/> component that is already withTableParams() connected, formNameFromWithTableParamsCall: " + formNameFromWithTPCall);
      }

      if (ownProps.tableParams && !ownProps.tableParams.entities) {
        console.error("No entities array detected in tableParams object (<DataTable {...tableParams}/>). You need to call withQuery() after withTableParams() like: compose(withTableParams(), withQuery(something)). formNameFromWithTableParamsCall: " + formNameFromWithTPCall);
      } //short circuit because we've already run this logic


      return {};
    }

    var formNameFromWithTableParamsCall;

    if (isLocalCall) {
      if (!noForm && (!formName || formName === "tgDataTable")) {
        console.error("Please pass a unique 'formName' prop to the locally connected <DataTable/> component with schema: ", schema);
      }
    } else {
      //in user instantiated withTableParams() call
      if (!formName || formName === "tgDataTable") {
        console.error("Please pass a unique 'formName' prop to the withTableParams() with schema: ", schema);
      } else {
        formNameFromWithTableParamsCall = formName;
      }
    }

    var tableConfig = getTableConfigFromStorage(formName);
    var formSelector = formValueSelector(formName);
    var currentParams = (urlConnected ? getCurrentParamsFromUrl(history.location) //important to use history location and not ownProps.location because for some reason the location path lags one render behind!!
    : formSelector(state, "reduxFormQueryParams")) || {};
    var selectedEntities = withSelectedEntities ? getRecordsFromReduxForm(state, formName) : undefined;
    var additionalFilterToUse = typeof additionalFilter === "function" ? additionalFilter.bind(_this, ownProps) : function () {
      return additionalFilter;
    };
    var additionalOrFilterToUse = typeof additionalOrFilter === "function" ? additionalOrFilter.bind(_this, ownProps) : function () {
      return additionalOrFilter;
    };
    var defaultsToUse = defaults; // make user set page size persist

    var userSetPageSize = (tableConfig == null ? void 0 : tableConfig.userSetPageSize) && parseInt(tableConfig.userSetPageSize, 10);

    if (!syncDisplayOptionsToDb && userSetPageSize) {
      defaultsToUse = defaultsToUse || {};
      defaultsToUse.pageSize = userSetPageSize;
    }

    var mapStateProps = _extends({
      history: history,
      urlConnected: urlConnected,
      withSelectedEntities: withSelectedEntities,
      formName: formName,
      defaults: defaultsToUse,
      isInfinite: isInfinite,
      isSimple: isSimple,
      withPaging: withPaging,
      doNotCoercePageSize: doNotCoercePageSize,
      additionalFilter: additionalFilter,
      additionalOrFilter: additionalOrFilter,
      noOrderError: noOrderError,
      withDisplayOptions: withDisplayOptions,
      cellRenderer: cellRenderer,
      isLocalCall: isLocalCall,
      model: model,
      schema: schema,
      mergedOpts: mergedOpts
    }, getQueryParams({
      doNotCoercePageSize: doNotCoercePageSize,
      currentParams: currentParams,
      entities: mergedOpts.entities,
      // for local table
      urlConnected: urlConnected,
      defaults: defaultsToUse,
      schema: convertSchema(schema),
      isInfinite: isInfinite || isSimple && !withPaging,
      isLocalCall: isLocalCall,
      additionalFilter: additionalFilterToUse,
      additionalOrFilter: additionalOrFilterToUse,
      noOrderError: noOrderError,
      isCodeModel: isCodeModel,
      ownProps: mergedOpts
    }), {
      formNameFromWithTPCall: formNameFromWithTableParamsCall,
      randomVarToForceLocalStorageUpdate: formSelector(state, "localStorageForceUpdate"),
      currentParams: currentParams,
      selectedEntities: selectedEntities
    }, withSelectedEntities && typeof withSelectedEntities === "string" && (_ref = {}, _ref[withSelectedEntities] = selectedEntities, _ref), {
      initialValues: _extends({}, initialValues, {
        reduxFormSearchInput: currentParams.searchTerm
      })
    });

    return mapStateProps; // return { ...mergedOpts, ...mapStateProps };
  };

  var mapDispatchToProps = function mapDispatchToProps(dispatch, ownProps) {
    if (ownProps.isTableParamsConnected) {
      return {};
    }

    var mergedOpts = getMergedOpts(topLevelOptions, ownProps);
    var formName = mergedOpts.formName,
        urlConnected = mergedOpts.urlConnected,
        history = mergedOpts.history,
        defaults = mergedOpts.defaults,
        onlyOneFilter = mergedOpts.onlyOneFilter;

    function updateSearch(val) {
      setTimeout(function () {
        dispatch(change(formName, "reduxFormSearchInput", val || ""));
      });
    }

    var setNewParams;

    if (urlConnected) {
      setNewParams = function setNewParams(newParams) {
        setCurrentParamsOnUrl(newParams, history.replace);
        dispatch(change(formName, "reduxFormQueryParams", newParams)); //we always will update the redux params as a workaround for withRouter not always working if inside a redux-connected container https://github.com/ReactTraining/react-router/issues/5037
      };
    } else {
      setNewParams = function setNewParams(newParams) {
        dispatch(change(formName, "reduxFormQueryParams", newParams));
      };
    }

    return {
      bindThese: makeDataTableHandlers({
        setNewParams: setNewParams,
        updateSearch: updateSearch,
        defaults: defaults,
        onlyOneFilter: onlyOneFilter
      }),
      dispatch: dispatch
    };
  };

  function mergeProps(stateProps, dispatchProps, ownProps) {
    if (ownProps.isTableParamsConnected) {
      return ownProps;
    }

    var currentParams = stateProps.currentParams,
        formName = stateProps.formName;
    var boundDispatchProps = {}; //bind currentParams to actions

    Object.keys(dispatchProps.bindThese).forEach(function (key) {
      var action = dispatchProps.bindThese[key];

      boundDispatchProps[key] = function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        action.apply(void 0, args.concat([currentParams]));
      };
    });

    var variables = stateProps.variables,
        selectedEntities = stateProps.selectedEntities,
        mergedOpts = stateProps.mergedOpts,
        restStateProps = _objectWithoutPropertiesLoose(stateProps, ["variables", "selectedEntities", "mergedOpts"]);

    var changeFormValue = function changeFormValue() {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      return dispatchProps.dispatch(change.apply(void 0, [formName].concat(args)));
    };

    var tableParams = _extends({
      changeFormValue: changeFormValue,
      selectedEntities: selectedEntities
    }, ownProps.tableParams, restStateProps, boundDispatchProps, {
      form: formName,
      //this will override the default redux form name
      isTableParamsConnected: true //let the table know not to do local sorting/filtering etc.

    });

    var allMergedProps = _extends({}, ownProps, mergedOpts, {
      variables: stateProps.variables,
      selectedEntities: stateProps.selectedEntities,
      tableParams: tableParams
    });

    return allMergedProps;
  }

  function addFormTracking(Component) {
    return function (props) {
      var formTracker = useContext(TableFormTrackerContext);
      var formName = props.formName;
      useEffect(function () {
        if (formTracker.isActive && !formTracker.formNames.includes(formName)) {
          formTracker.pushFormName(formName);
        }
      }, [formTracker, formName]);
      return /*#__PURE__*/React.createElement(Component, props);
    };
  }

  var toReturn = compose(connect(function (state, ownProps) {
    if (ownProps.isTableParamsConnected) {
      return {};
    }

    var _getMergedOpts = getMergedOpts(topLevelOptions, ownProps),
        formName = _getMergedOpts.formName;

    return {
      unusedProp: formValueSelector(formName)(state, "reduxFormQueryParams") || {} //tnr: we need this to trigger withRouter and force it to update if it is nested in a redux-connected container.. very ugly but necessary

    };
  }), branch(function (props) {
    //don't use withRouter if noRouter is passed!
    return !props.noRouter;
  }, withRouter), connect(mapStateToProps, mapDispatchToProps, mergeProps), pureNoFunc, addFormTracking);

  if (Component) {
    return toReturn(Component);
  }

  return toReturn;
}
/**
 * Given the options, get the schema. This enables the user to provide
 * a function instead of an object for the schema.
 * @param {Object} options Merged options
 */

function getSchema(options) {
  var schema = options.schema;
  if (isFunction(schema)) return schema(options);else return schema;
}